import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  DocumentsReferencesObjectScopeName,
  ODataOrRetrievedDocumentReference,
} from '../../../../components/v2/components/imageHandling/types/types';

import { RetrieveReferencedDocumentsParams } from '@interfaces/methods';
import { AppDispatch } from '@store/types';
import { isExpiredTimestamp } from '@helpers';
import {
  DOCUMENT_HIERARCHY_CONFIG,
  DOCUMENT_RELATED_IDS_KEYS,
  DOCUMENT_TYPE_TAG_KEY_MAP,
  DOCUMENTS_STORE_MODE,
  INDIRECT_LINKING_TEXT,
} from '../../../../components/imageHandling/common';
import { backendCall, createApiCall } from '@reducers/utils';
import { FORCE_RETRIEVE_FROM_SOURCE_KEY_EXPIRATION_TIME } from '@v2/components/imageHandling/constants';

export const retrieveReferencedDocuments = createAsyncThunk<
  ODataOrRetrievedDocumentReference[],
  RetrieveReferencedDocumentsParams,
  { dispatch: AppDispatch }
>('imageHandlingV2/retrieveReferencedDocuments', async (params, { dispatch }) => {
  const { documentKey, ids, abortSignal, storeMode = DOCUMENTS_STORE_MODE.DIRECT_INDIRECT_LINKING, retrieveAllDocuments = true } = params;

  // Check if forceRetrieveFromSource should be enabled based on expiration timestamp
  const isForceRetrieveFromSourceExpired = await isExpiredTimestamp(FORCE_RETRIEVE_FROM_SOURCE_KEY_EXPIRATION_TIME);

  const forceRetrieveFromSource = !isForceRetrieveFromSourceExpired;

  const scopeId = DOCUMENT_TYPE_TAG_KEY_MAP?.[documentKey];
  const scopeReferenceId = ids?.[0];

  if (!scopeId || !scopeReferenceId) {
    return [];
  }

  // Find the document config
  const documentConfig = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((config) => config.scopeKey && config.key === documentKey);

  if (!documentConfig) {
    return [];
  }

  // Execute as a single Promise-based abstraction
  return new Promise<ODataOrRetrievedDocumentReference[]>(async (resolve) => {
    try {
      const allDocuments: ODataOrRetrievedDocumentReference[] = [];
      const idsConfig: Record<string, number[]> = {};
      const visitedConfigsWithExceptions: string[] = [];
      const visitedConfigs: string[] = [];

      // Initialize idsConfig for all document types
      Object.entries(DOCUMENT_HIERARCHY_CONFIG).forEach(([key, c]) => {
        if (!idsConfig[key]) {
          idsConfig[key] = c.priority === documentConfig.priority ? ids : []; // Handle exception cases, where we can look in higher priority
        }
      });

      let initialPriority = documentConfig.priority;
      let config = documentConfig;

      while (initialPriority > 0) {
        // If exception config was visited we should skip it to not trap into infinite loop
        if (visitedConfigsWithExceptions.includes(config.key) || visitedConfigs.includes(config.key)) {
          initialPriority--;
          if (initialPriority === 0) break;

          let newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.priority === initialPriority);
          // If no config found at this priority, continue looking for lower priorities
          while (!newConfigItem && initialPriority > 1) {
            initialPriority--;
            newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.priority === initialPriority);
          }
          if (!newConfigItem) {
            break;
          }

          config = newConfigItem;
          continue;
        }

        // Check if we earlier visit current config. If not, add it to visited
        if (!visitedConfigs.includes(config.key)) {
          visitedConfigs.push(config.key);
        }

        // If config doesn't have any ids to retrieve
        if (!idsConfig[config.key]?.length) {
          let newConfigItem;

          // Update initialPriority and config with default variant or follow exception instructions
          if (!!config?.exceptionLinkToConfigItem) {
            // Add config into visited exception configs array
            visitedConfigsWithExceptions.push(config.key);

            newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.key === config?.exceptionLinkToConfigItem);
            if (!newConfigItem) {
              break;
            }
            initialPriority = newConfigItem.priority;
          } else {
            initialPriority--;
            if (initialPriority === 0) {
              break;
            }

            newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.priority === initialPriority);
            // If no config found at this priority, continue looking for lower priorities
            while (!newConfigItem && initialPriority > 1) {
              initialPriority--;
              newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.priority === initialPriority);
            }
            if (!newConfigItem) {
              break;
            }
          }

          config = newConfigItem;
          continue;
        }

        try {
          const response = await dispatch(
            backendCall(
              createApiCall((api) => api.imageHandling.retrieveReferencedDocuments, {
                signal: abortSignal,
                params: {
                  $type: config.requestType,
                  [config.scopeKey]: idsConfig[config.key],
                  retrieveAllDocuments,
                  forceRetrieveFromSource,
                },
              })
            )
          );

          // Validate response type
          const expectedResponseType = config.requestType?.replace('Request', 'Response');
          if (response?.['$type'] && response['$type'] !== expectedResponseType) {
            initialPriority--;
            if (initialPriority === 0) break;

            const newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.priority === initialPriority);
            if (!newConfigItem) break;
            config = newConfigItem;
            continue;
          }

          // Update related IDs
          for (const relatedIdsKey of Object.values(DOCUMENT_RELATED_IDS_KEYS)) {
            const relatedIds = response?.[relatedIdsKey as keyof typeof response];
            if (relatedIds && Array.isArray(relatedIds)) {
              const entityKey = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.responseKey === relatedIdsKey)?.key;

              if (entityKey) {
                idsConfig[entityKey] = [...new Set([...(idsConfig[entityKey] || []), ...(relatedIds as number[])])];
              }
            }
          }

          if (response?.documents) {
            const { scopeName: _scopeName, key } = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.responseType === response?.['$type']) ?? {};

            const isDirect = key === documentKey;

            const scopeName = (
              !isDirect && storeMode === DOCUMENTS_STORE_MODE.DIRECT_INDIRECT_LINKING ? INDIRECT_LINKING_TEXT : _scopeName
            ) as DocumentsReferencesObjectScopeName;

            allDocuments.push(...response.documents.map((d: ODataOrRetrievedDocumentReference) => ({ ...d, scopeId: config.scopeId, scopeName })));
          }

          // Update values for next iteration with default variant or follow exception instructions
          let newConfigItem;

          if (!!config?.exceptionLinkToConfigItem) {
            // Add config into visited exception configs array
            visitedConfigsWithExceptions.push(config.key);

            newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.key === config?.exceptionLinkToConfigItem);
            if (!newConfigItem) {
              break;
            }
            initialPriority = newConfigItem.priority;
          } else {
            initialPriority--;
            if (initialPriority === 0) {
              break;
            }

            newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.priority === initialPriority);
            // If no config found at this priority, continue looking for lower priorities
            while (!newConfigItem && initialPriority > 1) {
              initialPriority--;
              newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.priority === initialPriority);
            }
            if (!newConfigItem) {
              break;
            }
          }

          config = newConfigItem;
        } catch (error) {
          // Handle error gracefully
          console.error('Error retrieving referenced documents:', error);
          break;
        }
      }

      const sortedDocuments = sortDocumentsByDateField({ documents: filterDocumentsByContentHash(allDocuments) });
      resolve(sortedDocuments);
    } catch (error) {
      console.error('Error in retrieveReferencedDocuments:', error);
      resolve([]);
    }
  });
});

//helper functions
export const sortDocumentsByDateField = ({
  documents,
  field = 'createdOn',
  direction = 'desc',
}: {
  documents: ODataOrRetrievedDocumentReference[];
  field?: keyof ODataOrRetrievedDocumentReference;
  direction?: 'asc' | 'desc';
}) => {
  return [...documents].sort((a, b) => {
    const aValue = a[field];
    const bValue = b[field];

    // Check if values are valid date strings
    const isAValidDate = typeof aValue === 'string' && !isNaN(Date.parse(aValue));
    const isBValidDate = typeof bValue === 'string' && !isNaN(Date.parse(bValue));

    // If both are valid dates, compare them
    if (isAValidDate && isBValidDate) {
      return direction === 'desc'
        ? Date.parse(bValue as string) - Date.parse(aValue as string)
        : Date.parse(aValue as string) - Date.parse(bValue as string);
    }

    // If dates are not valid, skip sorting (return 0)
    return 0;
  });
};

export const filterDocumentsByContentHash = (documents: ODataOrRetrievedDocumentReference[]) => {
  const seenHashes = new Set<string>();

  const filtered =
    documents?.filter((d) => {
      if (!d?.contentHash) return false;

      if (seenHashes.has(d.contentHash)) {
        return false; // Skip duplicate
      }

      seenHashes.add(d.contentHash);
      return true; // Keep first occurrence
    }) || [];

  return filtered;
};
